import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { MarkdownRenderer } from '@/features/accountability/components/MarkdownRenderer';
import { usePublishedPage } from '../hooks/usePublishedPage';
import type { PublishedPageSlug } from '../api/dms-pages';

function DmsPublishedPage({ slug }: { slug: PublishedPageSlug }) {
  const { data, isLoading, isError, error } = usePublishedPage(slug);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-sm text-muted-foreground">Loading from DMS…</p>
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-8 space-y-2">
          <p className="text-sm font-medium text-destructive">
            Could not load this page from DMS.
          </p>
          <p className="text-xs text-muted-foreground">
            {(error as Error | undefined)?.message ?? 'Unknown error'}
          </p>
          <p className="text-xs text-muted-foreground">
            Expected: a DMS document tagged <code>published_page = {slug}</code>.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-1">
        DMS · {data.documentName} · v{data.version} ·{' '}
        {new Date(data.updatedAt).toLocaleString()}
      </p>
      <Card>
        <CardContent className="pt-6">
          <MarkdownRenderer>{data.content}</MarkdownRenderer>
        </CardContent>
      </Card>
    </div>
  );
}

type ActiveTab = 'vto' | 'playing-to-win' | 'bmc' | 'value-proposition';

const TABS: { value: ActiveTab; label: string }[] = [
  { value: 'vto', label: 'V/TO' },
  { value: 'playing-to-win', label: 'Playing to Win' },
  { value: 'bmc', label: 'Business Model Canvas' },
  { value: 'value-proposition', label: 'Value Proposition' },
];

const VALID_TABS: ActiveTab[] = ['vto', 'playing-to-win', 'bmc', 'value-proposition'];
const ACTIVE_TAB_STORAGE_KEY = 'atek_vto_active_tab';

export function VtoPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) as ActiveTab | null;
    return saved && VALID_TABS.includes(saved) ? saved : 'vto';
  });

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'vto' && <DmsPublishedPage slug="vto" />}
      {activeTab === 'playing-to-win' && <DmsPublishedPage slug="playing_to_win" />}
      {activeTab === 'bmc' && <DmsPublishedPage slug="bmc" />}
      {activeTab === 'value-proposition' && <DmsPublishedPage slug="vpc" />}
    </div>
  );
}
